/**
 * weeklySchedule.js
 *
 * Client-side resolver for `practitioner_weekly_schedule` rows.
 *
 * Table / endpoint:
 *   GET /api/practitioner-weekly-schedules/?clinic=<code>  (or ?practitioner_id=<id>)
 *   → rows: { id, practitioner, schedule, effective_start, effective_end, is_base, ... }
 *   → schedule format: { monday: { start: "12:00 PM", end: "8:00 PM" }, ... }
 *     Days with no shift are simply absent from the object.
 *
 * Precedence — MUST stay in step with PractitionerWeeklySchedule.pick_for_date in
 * backend/apps/practitioners/models.py:
 *   1. Rows whose effective_start..effective_end contains the date (inclusive).
 *      If several overlap, the NARROWEST range wins; the highest id breaks an exact tie.
 *   2. The base row (effective_start and effective_end both null) — the fallback for
 *      every date no dated row covers.
 * So a dated row overrides the base row inside its range, and only inside it.
 *
 * Dates are handled as "YYYY-MM-DD" strings throughout: ISO dates compare correctly
 * with < and <=, which sidesteps timezone drift from parsing them into Date objects.
 */

export const WEEK_DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

/** True for the unbounded fallback row. */
export function isBaseScheduleRow(row) {
  return !row?.effective_start && !row?.effective_end;
}

/** True if this row's effective range contains dateStr. Always false for the base row. */
export function rowCoversDate(row, dateStr) {
  if (!row || !dateStr || isBaseScheduleRow(row)) return false;
  return row.effective_start <= dateStr && dateStr <= row.effective_end;
}

/** Days covered by the range; Infinity for the base row so it never counts as narrowest. */
export function scheduleRangeLength(row) {
  if (isBaseScheduleRow(row)) return Infinity;
  const start = new Date(`${row.effective_start}T00:00:00`);
  const end = new Date(`${row.effective_end}T00:00:00`);
  return Math.round((end - start) / 86400000) + 1;
}

/**
 * The row that applies to one practitioner on dateStr, or null.
 * Pass dateStr = null/undefined to ask only for the base row.
 */
export function pickScheduleRow(rows, dateStr) {
  const list = rows ?? [];
  if (dateStr) {
    const covering = list.filter((row) => rowCoversDate(row, dateStr));
    if (covering.length > 0) {
      // Narrowest range first; newest row (highest id) breaks an exact tie.
      covering.sort((a, b) => scheduleRangeLength(a) - scheduleRangeLength(b) || b.id - a.id);
      return covering[0];
    }
  }
  return list.find(isBaseScheduleRow) ?? null;
}

/** The schedule object in effect on dateStr (base schedule when dateStr is omitted). */
export function scheduleForDate(rows, dateStr) {
  return pickScheduleRow(rows, dateStr)?.schedule ?? {};
}

/** The { start, end } slot for one day of the week on dateStr, or null. */
export function slotForDate(rows, dateStr, dayKey) {
  const slot = scheduleForDate(rows, dateStr)[dayKey];
  return slot?.start && slot?.end ? slot : null;
}

/** The practitioner's recurring template, ignoring every dated variant. */
export function baseSchedule(rows) {
  return rows?.find(isBaseScheduleRow)?.schedule ?? {};
}

/** The base row itself — needed to decide between POST (create) and PATCH (update). */
export function baseScheduleRow(rows) {
  return rows?.find(isBaseScheduleRow) ?? null;
}

/**
 * Unwrap the API response. The list endpoint returns { count, results } but returns a
 * bare array under some callers, so accept either.
 */
export function scheduleRows(data) {
  return Array.isArray(data) ? data : (data?.results ?? []);
}

/**
 * Group a flat API response into { [practitionerId]: rows[] } so pages that render a
 * whole roster resolve per practitioner without re-scanning the full list per cell.
 */
export function groupSchedulesByPractitioner(data) {
  const rows = scheduleRows(data);
  const grouped = {};
  for (const row of rows) {
    (grouped[row.practitioner] ??= []).push(row);
  }
  return grouped;
}

// ── Break time ────────────────────────────────────────────────────────────────
// A day slot may carry an optional `breaks` array — several are allowed, so a day can
// have a lunch plus a short afternoon break:
//   { start: "9:00 AM", end: "5:00 PM", breaks: [{ start: "12:00 PM", end: "1:00 PM" }] }
// Backend counterpart: apps/practitioners/schedule_times.py. Keep the two in step.

const MINUTES_PER_DAY = 24 * 60;

/** "8:00 AM" → minutes since midnight, or null if unparseable. */
export function parseTimeToMinutes(timeStr) {
  const m = /(\d+):(\d+)\s*(AM|PM)/i.exec(String(timeStr ?? "").trim());
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mins = parseInt(m[2], 10);
  const period = m[3].toUpperCase();
  if (period === "PM" && h !== 12) h += 12;
  if (period === "AM" && h === 12) h = 0;
  return h * 60 + mins;
}

/** The breaks on a day slot, always an array. */
export function slotBreaks(slot) {
  return Array.isArray(slot?.breaks) ? slot.breaks : [];
}

/**
 * The minute ranges actually worked on this day — the shift window with each break cut
 * out. Returns [[startMin, endMin], ...]; empty when the slot has no usable start/end.
 */
export function busyIntervals(slot) {
  const start = parseTimeToMinutes(slot?.start);
  let end = parseTimeToMinutes(slot?.end);
  if (start === null || end === null) return [];
  if (end <= start) end += MINUTES_PER_DAY; // shift crosses midnight

  let intervals = [[start, end]];
  for (const br of slotBreaks(slot)) {
    let bs = parseTimeToMinutes(br?.start);
    let be = parseTimeToMinutes(br?.end);
    if (bs === null || be === null) continue;
    // Lift the break into the shift's frame so a 12:30 AM break inside a 10 PM–2 AM
    // shift lands after the start, not 22 hours before it.
    if (bs < start) bs += MINUTES_PER_DAY;
    if (be <= bs) be += MINUTES_PER_DAY;

    const remaining = [];
    for (const [s, e] of intervals) {
      if (be <= s || bs >= e) {
        remaining.push([s, e]);
        continue;
      }
      if (bs > s) remaining.push([s, Math.min(bs, e)]);
      if (be < e) remaining.push([Math.max(be, s), e]);
    }
    intervals = remaining;
  }
  return intervals;
}

/** Minutes actually worked on this day — shift length minus every break. */
export function workingMinutes(slot) {
  return busyIntervals(slot).reduce((total, [s, e]) => total + (e - s), 0);
}

/** Hours actually worked on this day, rounded to 1dp. Null when the slot is incomplete. */
export function slotHours(slot) {
  if (!slot?.start || !slot?.end) return null;
  return Math.round((workingMinutes(slot) / 60) * 10) / 10;
}

/** Total break minutes on a day — for showing "8h shift, 1h break" style summaries. */
export function breakMinutes(slot) {
  const start = parseTimeToMinutes(slot?.start);
  let end = parseTimeToMinutes(slot?.end);
  if (start === null || end === null) return 0;
  if (end <= start) end += MINUTES_PER_DAY;
  return end - start - workingMinutes(slot);
}

/**
 * Validate a day's breaks, returning a human-readable problem or null when they're fine.
 *
 * Mirrors _validate_day_breaks in backend/apps/practitioners/serializers.py so the editors
 * can block a bad break before saving instead of surfacing a 400. Same rules: both times
 * required, not equal, inside the shift, no overlap between breaks, and not covering the
 * whole shift.
 */
export function breakError(slot) {
  const breaks = slotBreaks(slot);
  if (breaks.length === 0) return null;

  const shiftStart = parseTimeToMinutes(slot?.start);
  const rawShiftEnd = parseTimeToMinutes(slot?.end);
  if (shiftStart === null || rawShiftEnd === null) {
    return "Set the day's start and end times before adding a break.";
  }
  // A shift ending at or before its start crosses midnight (10 PM – 2 AM).
  const crossesMidnight = rawShiftEnd <= shiftStart;
  const shiftEnd = crossesMidnight ? rawShiftEnd + MINUTES_PER_DAY : rawShiftEnd;

  const seen = [];
  for (let i = 0; i < breaks.length; i++) {
    const br = breaks[i];
    const label = breaks.length > 1 ? `Break ${i + 1}` : "Break";
    const rawStart = parseTimeToMinutes(br?.start);
    const rawEnd = parseTimeToMinutes(br?.end);

    if (rawStart === null || rawEnd === null) {
      return `${label}: pick both a from and a to time.`;
    }
    if (rawStart === rawEnd) {
      return `${label}: the from and to times can't be the same.`;
    }
    // Only meaningful when the shift itself doesn't wrap: inside an overnight shift a
    // break really can have a "to" time numerically below its "from".
    if (!crossesMidnight && rawEnd < rawStart) {
      return `${label}: the from time (${br.start}) must be before the to time (${br.end}).`;
    }

    let start = rawStart;
    let end = rawEnd;
    if (start < shiftStart) start += MINUTES_PER_DAY;
    if (end <= start) end += MINUTES_PER_DAY;
    if (start < shiftStart || end > shiftEnd) {
      return `${label}: ${br.start}–${br.end} must fall inside ${slot.start}–${slot.end}.`;
    }
    for (const [otherStart, otherEnd, otherLabel] of seen) {
      if (start < otherEnd && otherStart < end) {
        return `${label} overlaps ${otherLabel}.`;
      }
    }
    seen.push([start, end, label]);
  }

  if (workingMinutes(slot) === 0) {
    return "Breaks cover the whole shift — turn the day off instead.";
  }
  return null;
}

/** First break problem across a whole draft schedule, or null when every day is fine. */
export function scheduleBreakError(draft, dayKeys = WEEK_DAY_KEYS) {
  for (const key of dayKeys) {
    const slot = draft?.[key];
    if (!slot?.start || !slot?.end) continue;
    const err = breakError(slot);
    if (err) return { dayKey: key, message: err };
  }
  return null;
}

/** "12:00 PM–1:00 PM, 3:00 PM–3:15 PM" for display, or "" when there are no breaks. */
export function formatBreaks(slot) {
  return slotBreaks(slot)
    .filter((br) => br?.start && br?.end)
    .map((br) => `${br.start}–${br.end}`)
    .join(", ");
}

/** Only the days with both a start and an end — the shape the API expects on write. */
export function compactSchedule(draft, dayKeys = WEEK_DAY_KEYS) {
  const out = {};
  for (const key of dayKeys) {
    const day = draft?.[key];
    if (day?.start && day?.end) {
      // Half-filled breaks are dropped rather than sent — the backend rejects a break
      // missing either end, which would block saving the whole schedule.
      const breaks = slotBreaks(day).filter((br) => br?.start && br?.end);
      out[key] = {
        start: day.start,
        end: day.end,
        ...(day.type ? { type: day.type } : {}),
        ...(breaks.length
          ? { breaks: breaks.map((br) => ({ start: br.start, end: br.end })) }
          : {}),
      };
    }
  }
  return out;
}
